import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, AlertCircle, CheckCircle2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * ConciergePaymentPanel — embedded inside the listing edit form.
 * Lets the owner select an approved partner from the directory (filtered by listing region/city),
 * set a revenue share % (free input 0–30), and optionally route the cleaning fee to the concierge.
 * Stripe transfer is created automatically on each successful booking.
 */
export default function ConciergePaymentPanel({
  listingCity,
  listingRegion,
  partnerId,
  sharePct,
  givesCleaning,
  cleaningFee = 0,
  pricePerNight = 0,
  onChange,
}) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = {};
    if (listingCity) params.city = listingCity;
    if (listingRegion) params.region = listingRegion;
    api.get("/partners", { params })
      .then(({ data }) => setPartners(data))
      .finally(() => setLoading(false));
  }, [listingCity, listingRegion]);

  const selected = partners.find((p) => p.id === partnerId);
  const enabled = !!partnerId && sharePct > 0;

  // Preview math for one example night
  const examplePct = sharePct || 0;
  const exampleSubtotal = pricePerNight || 100;
  const conciergeShare = +(exampleSubtotal * (examplePct / 100)).toFixed(2);
  const conciergeTotal = +(conciergeShare + (givesCleaning ? cleaningFee : 0)).toFixed(2);
  const platformFee = +(exampleSubtotal * 0.1).toFixed(2);
  const ownerNet = +(exampleSubtotal - platformFee - conciergeShare + (givesCleaning ? 0 : cleaningFee)).toFixed(2);

  return (
    <div className="border border-black/10 bg-white" data-testid="concierge-pay-panel">
      <div className="px-4 py-3 border-b border-black/10 bg-[#FAFAFA] flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-[#7A1535]" />
        <div className="text-[11px] uppercase tracking-[0.25em] font-bold">Conciergerie & paiement automatique</div>
      </div>

      <div className="p-5 space-y-4">
        <p className="text-sm text-neutral-600 leading-relaxed">
          Déléguez la gestion à un partenaire MSB : sa part sera versée <strong>automatiquement sur son compte Stripe</strong> à chaque réservation. Vous touchez le reste, MSB sa commission, en même temps.
        </p>

        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Conciergerie liée à cette annonce</div>
          {loading ? (
            <div className="text-sm text-neutral-500 p-3 border border-black/10">Chargement…</div>
          ) : (
            <select
              data-testid="cp-partner"
              value={partnerId || ""}
              onChange={(e) => onChange({ concierge_partner_id: e.target.value || null })}
              className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A] bg-white"
            >
              <option value="">Aucune (je gère moi-même)</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.company_name} — {p.partner_type === "conciergerie" ? "Conciergerie" : "Ménage"} · {(p.regions || []).join(", ")}
                </option>
              ))}
            </select>
          )}
          {!loading && partners.length === 0 && (
            <p className="text-xs text-neutral-500 mt-2">
              Aucun partenaire référencé dans votre secteur. <Link to="/partenaires" className="underline">Découvrir l'annuaire</Link> ou <Link to="/devenir-partenaire" className="underline">inviter un prestataire</Link>.
            </p>
          )}
        </label>

        {partnerId && selected && (
          <>
            {!selected.has_payment_account_warning && (
              <div className="p-3 bg-[#FFFBEA] border border-[#C9A14A]/40 text-xs flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#9A4D1F]" />
                <span>Le versement automatique nécessite que <strong>{selected.company_name}</strong> ait connecté son compte Stripe. Sinon, la réservation ne pourra pas être confirmée.</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Part de la conciergerie (% de la nuitée)</div>
                <div className="relative">
                  <input
                    data-testid="cp-share-pct"
                    type="number"
                    min={0}
                    max={50}
                    step={0.5}
                    value={sharePct || ""}
                    onChange={(e) => onChange({ concierge_revenue_share_percent: Number(e.target.value) || 0 })}
                    placeholder="15"
                    className="w-full border border-black/15 p-3 pr-10 outline-none focus:border-[#0A0A0A]"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500 font-bold">%</span>
                </div>
                <p className="text-[11px] text-neutral-500 mt-1">Typique : 15–25% pour une conciergerie clé en main</p>
              </label>

              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Frais de ménage versés à la conciergerie</div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="cp-cleaning-yes"
                    onClick={() => onChange({ concierge_gets_cleaning_fee: true })}
                    className={`flex-1 p-3 border font-semibold text-sm ${givesCleaning ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}
                  >
                    Oui
                  </button>
                  <button
                    type="button"
                    data-testid="cp-cleaning-no"
                    onClick={() => onChange({ concierge_gets_cleaning_fee: false })}
                    className={`flex-1 p-3 border font-semibold text-sm ${!givesCleaning ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}
                  >
                    Non
                  </button>
                </div>
              </label>
            </div>

            {/* Live preview */}
            {enabled && (
              <div className="border border-black/10 bg-[#F5F2EC] p-4" data-testid="cp-preview">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-3">Simulation pour 1 nuit à {exampleSubtotal}€{givesCleaning && cleaningFee > 0 ? ` + ${cleaningFee}€ ménage` : ""}</div>
                <div className="space-y-1.5 text-sm">
                  <Row label="Voyageur paie" value={exampleSubtotal + cleaningFee} bold />
                  <Row label={`Commission MSB (10%)`} value={-platformFee} muted />
                  <Row label={`Conciergerie — ${selected.company_name}`} value={conciergeTotal} accent />
                  <Row label="Vous (propriétaire)" value={ownerNet + (givesCleaning ? 0 : cleaningFee)} accent bold />
                </div>
                <p className="text-[11px] text-neutral-600 mt-3 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-[#166534]" /> Chacun reçoit sa part automatiquement via Stripe à la confirmation du paiement.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, bold = false, muted = false, accent = false }) {
  const sign = value < 0 ? "−" : "";
  const abs = Math.abs(value).toFixed(2);
  return (
    <div className={`flex items-baseline justify-between ${bold ? "font-bold" : ""} ${muted ? "text-neutral-500" : ""}`}>
      <span>{label}</span>
      <span className={accent ? "text-[#7A1535] font-bold" : ""}>{sign}{abs} €</span>
    </div>
  );
}
